import client from 'core/axios';
import handleError from 'core/axios/handleError';
import {
  handleJSONResponse,
  handleFileDownloadResponse,
} from 'core/axios/handleResponse';
import RequestMethod from 'core/axios/RequestMethod';

export const getSearchCourseFilterContext = ({ version = 'v1', params = {} }) =>
  client
    .request({
      url: `${version}/cp-courses/context/`,
      method: RequestMethod.GET,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const retrieveCourses = ({
  version = 'v1',
  params = {},
  cancelToken = null,
}) =>
  client
    .request({
      url: `${version}/cp-courses/courses/`,
      method: RequestMethod.GET,
      params,
      cancelToken,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const searchCourses = ({
  version = 'v1',
  studentId,
  studentPlanUUID,
  params,
  cancelToken = null,
}) =>
  client
    .request({
      url: `${version}/cp-courses/courses/${studentId}/${studentPlanUUID}`,
      method: RequestMethod.GET,
      params,
      cancelToken,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const searchCourseCatalog = ({
  version = 'v1',
  params,
  cancelToken = null,
}) =>
  client
    .request({
      url: `${version}/cp-courses/course/`,
      method: RequestMethod.GET,
      params,
      cancelToken,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const getCourseTags = ({ version = 'v1', params = {} }) =>
  client
    .request({
      url: `${version}/cp-courses/tags/`,
      method: RequestMethod.GET,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const getCourseSubjects = ({ version = 'v1', params = {} }) =>
  client
    .request({
      url: `${version}/cp-courses/subjects/`,
      method: RequestMethod.GET,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const getCourseOfferingsList = ({ params = {}, version = 'v1' } = {}) =>
  client
    .request({
      url: `${version}/cp-courses/course`,
      method: RequestMethod.GET,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const getCourseDetails = ({
  params = {},
  version = 'v1',
  courseId,
} = {}) =>
  client
    .request({
      url: `${version}/cp-courses/course/${courseId}/`,
      method: RequestMethod.GET,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const retrieveDistrictContext = ({ version = 'v1', params = {} } = {}) =>
  client
    .request({
      url: `${version}/cp-courses/context/`,
      method: RequestMethod.GET,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const retrieveDistrictConfiguration = ({
  params = {},
  version = 'v1',
} = {}) =>
  client
    .request({
      url: `${version}/cp-config/district/config/`,
      method: RequestMethod.GET,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const createCourseDetails = ({
  params = {},
  version = 'v1',
  data = {},
} = {}) =>
  client
    .request({
      url: `${version}/cp-courses/course/`,
      method: RequestMethod.POST,
      params,
      data,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const updateCourseDetails = ({
  params = {},
  version = 'v1',
  data = {},
  courseId,
} = {}) =>
  client
    .request({
      url: `${version}/cp-courses/course/${courseId}/`,
      method: RequestMethod.PATCH,
      params,
      data,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const createCourseOfferingHash = ({
  params,
  data = {},
  version = 'v1',
}) =>
  client
    .request({
      url: `${version}/cp-courses/courses/courses-hash/`,
      method: RequestMethod.POST,
      params,
      data,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const createCourseOfferingReportCSV = ({ data = {}, version = 'v1' }) =>
  client
    .request({
      url: `${version}/cp-courses/course-master-export/`,
      method: RequestMethod.POST,
      data,
    })
    .then(handleFileDownloadResponse)
    .catch(handleError);

export const updateCourseOffering = ({ data = {}, version = 'v1' }) =>
  client
    .request({
      url: `${version}/cp-courses/courses/bulk-update/`,
      method: RequestMethod.PATCH,
      data,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const getRelatedConfiguration = ({ courseUUID, version = 'v3' }) =>
  client
    .request({
      url: `${version}/cp-courses/course/${courseUUID}/related-configuration/`,
      method: RequestMethod.GET,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const getGradPlanRelatedConfiguration = ({
  courseUUID,
  params,
  version = 'v3',
}) =>
  client
    .request({
      url: `${version}/cp-courses/course/${courseUUID}/related-grad-plan-configuration/`,
      method: RequestMethod.GET,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const getNonMasterCourseList = ({
  courseUUID,
  params,
  version = 'v3',
}) =>
  client
    .request({
      url: `${version}/cp-courses/course/${courseUUID}/non-master-course-list/`,
      method: RequestMethod.GET,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const removeNonMasterCourseRelation = ({
  courseUUID,
  params,
  version = 'v3',
}) =>
  client
    .request({
      url: `${version}/cp-courses/course/${courseUUID}/remove-non-master-course-relation/`,
      method: RequestMethod.POST,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const deleteCourse = ({ courseUUID, params, version = 'v1' }) =>
  client
    .request({
      url: `${version}/cp-courses/course/${courseUUID}/`,
      method: RequestMethod.DELETE,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const getRelatedSequence = ({ courseId, params, version = 'v3' }) =>
  client
    .request({
      url: `${version}/cp-courses/course/${courseId}/related-sequence/`,
      method: RequestMethod.GET,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const resetField = ({ courseId, data = {}, version = 'v3' }) =>
  client
    .request({
      url: `${version}/cp-courses/course/${courseId}/reset-modified/`,
      method: RequestMethod.POST,
      data,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const deleteEquivalentCourses = ({ data = {}, version = 'v1' }) =>
  client
    .request({
      url: `${version}/cp-courses/courses/equivalent-courses/`,
      method: RequestMethod.DELETE,
      data,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const getCourseNavigation = ({
  params = {},
  cancelToken = null,
  version = 'v1',
} = {}) =>
  client
    .request({
      url: `${version}/cp-courses/course/navigation/`,
      method: RequestMethod.GET,
      params,
      cancelToken,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const getCourseHistory = ({
  params = {},
  cancelToken = null,
  version = 'v1',
  courseId,
} = {}) =>
  client
    .request({
      url: `${version}/cp-courses/courses/${courseId}/changelog-history`,
      method: RequestMethod.GET,
      params,
      cancelToken,
    })
    .then(handleJSONResponse)
    .catch(handleError);
